import { ServiceOrder } from 'src/entities/order/model/order.model';
import { ServicePurchaseNote } from 'src/entities/purchase-note/model/purchase-note.model';

export interface ProgrammingServicePax {
  Pax?: string | null;
  estado?: boolean | null;
  type?: string | null;
  pasajero?: {
    firstName?: string | null;
    lastName?: string | null;
    restriccionesAlimentarias?: string | null;
  } | null;
}

/** Proveedor paint-ready de Core (`providerSnapshot` en getProgrammingServicesAndOrdersByFile). */
export interface ProgrammingServiceProvider {
  id?: string | null;
  nombre?: string | null;
  correoReserva?: string | null;
  razonSocial?: string | null;
  estadoProveedor?: string | null;
  tipoProveedor?: string | null;
  condicionesPagos?: {
    condicionPago?: string | null;
    plazoDiasFit?: number | null;
    plazoDiasGrupos?: number | null;
    plazoDiasSeries?: number | null;
  } | null;
}

/** Snapshot del servicio de catálogo (`serviceSnapshot`). */
export interface ProgrammingServiceSnapshot {
  locacion?: string | null;
}

export type ProgrammingServiceOrder = ServiceOrder;

/** Servicio de programación (getProgrammingServicesAndOrdersByFile) — campos derivados en Core. */
export interface ProgrammingService {
  /** itinerary_services.id — usar para createOrder y acciones de programación */
  id: string;
  /** itinerary_services.serviceId — referencia al catálogo de Sourcing */
  serviceId?: string | null;
  idItinerary?: string | null;
  productId?: string | null;
  ssffproduct?: boolean | null;
  serviceAdd?: boolean | null;
  day?: string | null;
  /** Fecha de operación del servicio (YYYY-MM-DD). Base del plazo para confirmar. */
  startDateService?: string | null;
  nombreServicio?: string | null;
  tipoServicio?: string | null;
  categoriaServicio?: string | null;
  tipoTarifaAlojamiento?: string | null;
  /** Tipos de unidad Core (`["SPRINTER_CORTA"]`). Legacy string → normalizar a `[string]`. */
  tipoDeUnidad?: string[] | null;
  mainGuide?: boolean | null;
  /** Total resuelto en Core (edit vs base, 2 dec). */
  total?: number | null;
  /** "D" | "S" */
  moneda?: string | null;
  /** Tipo de cambio del servicio (padre); usar en edit monto/pax de tickets. */
  tipoCambio?: number | null;
  /** null si el monto fue editado */
  costoUnitario?: number | null;
  paxQuantity?: number | null;
  tcQuantity?: number | null;
  order?: ProgrammingServiceOrder | null;
  /**
   * Scalar Core (ID plano, null si no tiene NP). Sin join de cabecera.
   * Al entrar a Alimentos → getPurchaseNotesByIds con estos ids.
   */
  purchaseNoteId?: string | null;
  /** Detalle local (getPurchaseNotesByIds / createPurchaseNote). No viene en el listado general. */
  purchaseNote?: ServicePurchaseNote | null;
  hasDocument?: boolean | null;
  hasInvalidFechaOperacion?: boolean | null;
  /** Padre TICKET: true si algún ticket ya tiene OS. */
  hasOrderedTickets?: boolean | null;
  /** false = OS preliminar, el costo sigue editable. */
  orderIssuanceFrozen?: boolean | null;
  menu?: { nombreMenu?: string | null } | null;
  /** Solo expand de pasajeros; no usar para conteos. */
  lstPaX?: ProgrammingServicePax[];
  /** Proveedor Core (reemplaza objProvider en esta query). */
  providerSnapshot?: ProgrammingServiceProvider | null;
  /** Locación / metadata de catálogo. */
  serviceSnapshot?: ProgrammingServiceSnapshot | null;
}

/** Proveedor Core de un servicio de programación (null-safe). */
export function getProgrammingProvider(
  row:
    | {
        providerSnapshot?: ProgrammingServiceProvider | null;
        objProvider?: ProgrammingServiceProvider | null;
      }
    | null
    | undefined
): ProgrammingServiceProvider | null {
  return row?.providerSnapshot ?? row?.objProvider ?? null;
}

/** Locación Core del servicio (aventura / diálogos). */
export function getProgrammingLocacion(
  row:
    | {
        serviceSnapshot?: ProgrammingServiceSnapshot | null;
        locacion?: string | null;
        objServicio?: { locacion?: string | null } | null;
      }
    | null
    | undefined
): string | null {
  return (
    row?.serviceSnapshot?.locacion ??
    row?.locacion ??
    row?.objServicio?.locacion ??
    null
  );
}

/** Id técnico del proveedor (filtros / changeProvider / createOrder). */
export function getProgrammingProviderId(
  row:
    | {
        providerSnapshot?: { id?: string | null } | null;
        objProvider?: { id?: string | null } | null;
        providerId?: string | null;
      }
    | null
    | undefined
): string | null {
  return (
    row?.providerSnapshot?.id ??
    row?.objProvider?.id ??
    row?.providerId ??
    null
  );
}

/**
 * Normaliza `tipoDeUnidad` de Core a `string[] | null`.
 * Acepta array o string legacy (`"SPRINTER_CORTA"` → `["SPRINTER_CORTA"]`).
 */
export function normalizeTipoDeUnidad(
  value: string[] | string | null | undefined
): string[] | null {
  if (value == null) {
    return null;
  }

  if (Array.isArray(value)) {
    const units = value
      .map((item) => (item == null ? '' : String(item).trim()))
      .filter(Boolean);
    return units.length ? units : null;
  }

  const legacy = String(value).trim();
  return legacy ? [legacy] : null;
}

/** Texto para UI a partir de `tipoDeUnidad` (array o legacy string). */
export function formatTipoDeUnidad(
  value: string[] | string | null | undefined
): string {
  return normalizeTipoDeUnidad(value)?.join(', ') ?? '';
}
